import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import patterbackground from "../images/pattern.jpeg";

interface PersonalizationDetailsModalProps {
  open: boolean;
  onClose: () => void;
  where: string;
  belovedName: string;
  churchName: string;
  burialLocation: string;
  onBelovedNameChange: (value: string) => void;
  onChurchNameChange: (value: string) => void;
  onBurialLocationChange: (value: string) => void;
  onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  photoPreview: string | null;
  onSubmit: () => void;
  onSkip: () => void;
  isSubmitting: boolean;
}

const PersonalisationModal: React.FC<PersonalizationDetailsModalProps> = ({
  open,
  onClose,
  where,
  belovedName,
  churchName,
  burialLocation,
  onBelovedNameChange,
  onChurchNameChange,
  onBurialLocationChange,
  onPhotoUpload,
  photoPreview,
  onSubmit,
  onSkip,
  isSubmitting,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: "#614338",
          color: "white",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${patterbackground})`,
            backgroundRepeat: "repeat",
            backgroundSize: "contain",
            opacity: 0.1,
            zIndex: 0,
          }}
        />
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h4" fontWeight="bold">
            Personalization Details
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, mt: 1, color: colors.primary[500] }}
        >
          Share details to help us provide a more meaningful quote
        </Typography>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {where === "Church" ? (
          <>
            {/* Beloved Name */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Full Name of The Beloved{" "}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter Full Name"
                value={belovedName}
                onChange={(e) => onBelovedNameChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FavoriteIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>

            {/* Church Name */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Specific Name of Church{" "}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., St. Mary's Church"
                value={churchName}
                onChange={(e) => onChurchNameChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>
          </>
        ) : where === "Burial Ground" ? (
          <>
            {/* Burial Ground Name */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Name of The Beloved{" "}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter name"
                value={belovedName}
                onChange={(e) => onBelovedNameChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FavoriteIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Specific Location of Burial Ground{" "}
                <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., Borrowdale Cemetery, Plot 123"
                value={burialLocation}
                onChange={(e) => onBurialLocationChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Name of The Beloved{" "}
              <span style={{ color: theme.palette.error.main }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter name"
              value={belovedName}
              onChange={(e) => onBelovedNameChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FavoriteIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Photo of Beloved{" "}
            <span style={{ color: "text.secondary" }}>(Optional)</span>
          </Typography>
          <Button
            component="label"
            fullWidth
            variant="outlined"
            sx={{
              py: photoPreview ? 1.5 : 4,
              borderRadius: 2,
              borderStyle: "solid",
              borderWidth: 2,
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "&:hover": {
                borderColor: colors.primary[400],
                bgcolor: `${colors.primary[800]} !important`,
                borderStyle: "dashed",
                borderWidth: 1,
              },
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={onPhotoUpload}
            />

            {!photoPreview && (
              <>
                <UploadIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight="bold">
                  Click to upload photo
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  PNG, JPG up to 10MB
                </Typography>
              </>
            )}

            {photoPreview && (
              <Box
                component="img"
                src={photoPreview}
                sx={{
                  mt: { xs: 0, md: 2 },
                  maxWidth: "100%",
                  maxHeight: 200,
                  borderRadius: 1,
                }}
              />
            )}
            {photoPreview && (
              <Typography variant="body2" mb={1}>
                Click to change photo
              </Typography>
            )}
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onSkip}
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: "text.primary",
              borderColor: "divider",
              backgroundColor: colors.primary[800],
              boxShadow: 0,
            }}
          >
            {isSubmitting ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} />
                Submitting...
              </Box>
            ) : (
              "Skip"
            )}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onSubmit}
            sx={{
              py: 1.5,
              background: colors.primary[400],
              color: "white",
              boxShadow: 0,
            }}
          >
            {isSubmitting ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} />
                Submitting...
              </Box>
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalisationModal;
